
import React, { useState } from 'react';
import { X, Play, LayoutGrid, Settings, Plus, Folder, FolderOpen, ChevronRight, ChevronDown } from 'lucide-react';
import { CustomDeck, CustomDeckFolder } from '../types';

interface DeckSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  decks: CustomDeck[];
  onSelectDeck: (deck: CustomDeck) => void;
  onManageDecks: () => void;
  user: any;
}

const DeckSelectionModal: React.FC<DeckSelectionModalProps> = ({ isOpen, onClose, decks, onSelectDeck, onManageDecks, user }) => {
  const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({});

  if (!isOpen) return null;

  const folders: CustomDeckFolder[] = user?.customFolders || [];

  const renderDeckItem = (deck: CustomDeck, isNested = false) => (
    <button
      key={deck.id}
      onClick={() => onSelectDeck(deck)}
      className={`w-full group flex items-center justify-between p-4 bg-black/50 border border-zinc-800 hover:border-purple-500 hover:bg-purple-900/10 rounded-lg transition-all ${isNested ? 'border-l-2 border-l-zinc-700' : ''}`}
    >
      <div className="flex items-center gap-4">
        <div className="p-2 bg-zinc-800 rounded-lg group-hover:bg-purple-500/20 group-hover:text-purple-400 transition-colors text-zinc-400">
          <LayoutGrid size={20} />
        </div>
        <div className="text-left">
          <div className="text-white font-orbitron tracking-wider">{deck.name}</div>
          <div className="text-xs text-zinc-500 font-rajdhani uppercase tracking-widest">{deck.tricks.length} Tricks</div>
        </div>
      </div>
      <Play className="text-zinc-600 group-hover:text-purple-400 transition-colors" size={20} />
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-lg bg-zinc-900 border border-zinc-700 rounded-xl p-6 shadow-[0_0_30px_rgba(0,0,0,0.5)]">
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white">
          <X size={24} />
        </button>

        {/* Manage Decks Button (Moves user to Editor) */}
        <button 
            onClick={onManageDecks} 
            className="absolute top-4 left-4 text-zinc-500 hover:text-cyan-400 flex items-center gap-1 transition-colors"
            title="Deck Command Center"
        >
            <Settings size={18} />
            <span className="text-xs font-orbitron uppercase tracking-wider hidden sm:inline">Manage</span>
        </button>

        <h2 className="text-2xl font-orbitron font-bold text-white mb-2 text-center neon-glow-blue uppercase mt-8 sm:mt-0">
          Select Deck / 选择招式表
        </h2>
        <p className="text-center text-zinc-400 font-rajdhani mb-6 text-sm">
          Choose a custom deck to battle with. / 选择一个招式表进行对战。
        </p>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto overflow-x-hidden pr-2 custom-scrollbar">
          {!user ? (
             <div className="flex flex-col items-center justify-center py-8 gap-4 border-2 border-dashed border-zinc-800 rounded-xl">
               <div className="text-zinc-500 font-rajdhani text-center px-4">
                 Please log in to access or create custom decks. / 请先登录以访问或创建自定义招式表。
               </div>
               <button 
                  onClick={onManageDecks}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-900/30 text-blue-400 border border-blue-500/50 rounded-lg hover:bg-blue-900/50 hover:text-white transition-all font-orbitron text-xs uppercase tracking-widest shadow-[0_0_10px_rgba(59,130,246,0.2)]"
               >
                  Log In / 登录
               </button>
             </div>
          ) : decks.length === 0 && folders.length === 0 ? (
             <div className="flex flex-col items-center justify-center py-8 gap-4 border-2 border-dashed border-zinc-800 rounded-xl">
               <LayoutGrid size={32} className="text-zinc-600" />
               <div className="text-zinc-500 font-rajdhani text-center">
                 No custom decks found. / 暂无自定义招式表。
               </div>
               <button 
                  onClick={onManageDecks}
                  className="flex items-center gap-2 px-6 py-2 bg-cyan-900/30 text-cyan-400 border border-cyan-500/50 rounded-lg hover:bg-cyan-900/50 hover:text-white transition-all font-orbitron text-xs uppercase tracking-widest shadow-[0_0_10px_rgba(6,182,212,0.2)]"
               >
                  <Plus size={16} />
                  Create New Deck / 创建新招式表
               </button>
             </div>
          ) : (
            <div className="space-y-3">
              {folders.map(folder => {
                const folderDecks = decks.filter(d => d.folderId === folder.id);
                const isExpanded = expandedFolders[folder.id];
                return (
                  <div key={folder.id} className="space-y-2">
                    <button
                      onClick={() => setExpandedFolders(prev => ({ ...prev, [folder.id]: !isExpanded }))}
                      className="w-full flex items-center justify-between p-3 bg-zinc-900/80 border border-zinc-800 hover:border-yellow-500/50 hover:bg-yellow-900/10 rounded-lg transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-yellow-500">
                          {isExpanded ? <FolderOpen size={20} /> : <Folder size={20} />}
                        </div>
                        <div className="text-white font-orbitron tracking-wider text-sm">{folder.name}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] text-zinc-500 font-rajdhani uppercase tracking-widest">{folderDecks.length} Decks</span>
                        {isExpanded ? <ChevronDown size={16} className="text-zinc-500" /> : <ChevronRight size={16} className="text-zinc-500" />}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="space-y-2 border-l-2 border-zinc-800 ml-2 pl-2">
                        {folderDecks.length === 0 ? (
                          <div className="text-xs text-zinc-600 italic font-rajdhani py-2 px-4">Empty Folder</div>
                        ) : (
                          folderDecks.map(deck => renderDeckItem(deck, true))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {decks.filter(d => !d.folderId).map((deck) => renderDeckItem(deck))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeckSelectionModal;
